import { useCallback } from 'react'
import { NoteType, isPrivateNote, type Note } from '../utils/note'
import { usePrivateProjectNotes } from './useNotesFactory'
import useMoveNote, { type MoveNoteOption } from './useMoveNote'
import { type SidebarEntry } from '../modules/sidebar/SidebarBuilder'
import { trackEvent } from '../lib/analytics'

type MoveToArchiveOptions = {
  recordName: string
  noteType: NoteType
  children?: SidebarEntry[]
  descendenceIds?: string[]
}

/**
 * Hook to move private project notes and folders to the @Archive folder
 */
export function useMoveToArchive() {
  const { data: privateProjectNotes } = usePrivateProjectNotes()
  const moveNote = useMoveNote()

  const moveToArchive = useCallback(
    async ({
      recordName,
      noteType,
      children = [],
      descendenceIds = [],
    }: MoveToArchiveOptions) => {
      // Only handle private notes - team notes should not be archived
      if (!isPrivateNote(noteType)) {
        throw new Error('useMoveToArchive only supports private notes')
      }

      // Get the note to check if it's a special folder that shouldn't be moved to archive
      const noteToMove = privateProjectNotes?.map.get(recordName)
      const specialFolders = ['@Templates', '@Archive', '@Trash']
      if (noteToMove && specialFolders.includes(noteToMove.filename)) {
        throw new Error(
          `Cannot move special folder "${noteToMove.filename}" to archive. Special folders like @Templates, @Archive, and @Trash cannot be archived.`
        )
      }

      // Find the @Archive folder
      const archiveFolders = privateProjectNotes?.map
        ? [...privateProjectNotes.map.values()].filter(
            (note) => note.isFolder && note.filename === '@Archive'
          )
        : []

      // Check if the note is already in archive (has @Archive as parent)
      const archiveFolderId = archiveFolders[0]?.recordName
      if (noteToMove?.parent === archiveFolderId) {
        throw new Error('This item is already in the archive folder.')
      }

      if (archiveFolders.length === 0) {
        throw new Error(
          'Archive folder not found. Please refresh the page and try again.'
        )
      }

      if (archiveFolders.length > 1) {
        console.warn(
          `Found ${archiveFolders.length} @Archive folders. Using the first one.`
        )
      }

      const archiveFolder = archiveFolders[0]

      if (!archiveFolder.recordName) {
        throw new Error('@Archive folder recordName is undefined')
      }

      // Prepare the move operation
      const moveOptions: MoveNoteOption = {
        recordName,
        noteType,
        parent: {
          recordName: archiveFolder.recordName,
          noteType: NoteType.PROJECT_NOTE,
        },
        children,
        privateProjectNotes:
          privateProjectNotes?.map ?? new Map<string, Note>(),
        teamProjectNotes: new Map(), // Not needed for private notes
      }

      // Track the move to archive event
      trackEvent('WEB - Note Moved to Archive', {
        recordName,
        noteType,
        descendenceIds,
        archiveFolderId: archiveFolder.recordName,
      })

      // Execute the move
      return moveNote.mutateAsync(moveOptions)
    },
    [privateProjectNotes, moveNote]
  )

  return {
    moveToArchive,
    isLoading: moveNote.isLoading,
    error: moveNote.error,
    isSuccess: moveNote.isSuccess,
  }
}
